import type { StagedPlay } from "@/components/chat/chat-shell";
import { rowLines } from "@/components/fantasy-props/model";
import type { LegTool } from "@/convex/shares";
import { discrepancyShare, legsShare, rowShare, whaleShare, type PropsTool } from "@/lib/community/play";
import { oddsScreenHrefForLiveRow } from "@/lib/fantasy-props/odds-screen-link";
import { priceDisplay, type PricedRow } from "@/lib/fantasy-props/rows";
import { toolLinkHref } from "@/lib/tool-link";
import type { ArbLeg, ArbRow, Catalog, EventHeader, LowHoldRow } from "@/lib/odds-screen/live/types";
import type { DerivedRow } from "@/lib/prop-discrepancies/rows";
import { formatSideName, type MarketOpportunity } from "@/lib/whale-watch/whale-watch-shared";

const SEARCH_PAGE = { optimizer: "/optimizer", fantasy: "/optimizer/props" } as const;

export function stagePlay(tool: PropsTool, priced: PricedRow, catalog: Catalog | null): StagedPlay | null {
  const { row, event, book, weights } = priced;
  const display = priceDisplay(priced);
  if (display === null) return null;
  const href = oddsScreenHrefForLiveRow(catalog, display) ?? (tool === "sniper" ? "/ev-sniper" : toolLinkHref(SEARCH_PAGE[tool], { player_search: rowLines(display, event).title }));
  const preview = rowShare(tool, display, event, book, href, Date.now());
  return preview && { ref: { tool, league: row.league, book, rowId: row.id, href, weights }, preview };
}

export function stageWhale(opp: MarketOpportunity): StagedPlay | null {
  const side = opp.market.sides[0];
  if (side === undefined || !side.best_book) return null;
  const href = `/whale-watch?${new URLSearchParams({ q: formatSideName(side, opp.market.market) })}`;
  const preview = whaleShare(opp, href, Date.now());
  return preview && { ref: { tool: "whale", league: "all", book: side.best_book, rowId: opp.id, href }, preview };
}

export function stageDiscrepancy({ row, event, best }: DerivedRow): StagedPlay | null {
  const href = "/prop-discrepancies";
  const preview = discrepancyShare(row, event, best.book.book, best.side, href, Date.now());
  return preview && { ref: { tool: "discrepancies", league: row.league, book: best.book.book, side: best.side, rowId: row.id, href }, preview };
}

export function stageLegs(tool: LegTool, play: { readonly row: ArbRow | LowHoldRow; readonly event: EventHeader; readonly legs: readonly ArbLeg[] }): StagedPlay | null {
  const href = tool === "arbitrage" ? "/arbitrage" : "/low-hold";
  const preview = legsShare(tool, play.row, play.event, play.legs, href, Date.now());
  return preview && { ref: { tool, league: play.row.league, rowId: play.row.id, legs: play.legs.map(({ book, side, line }) => ({ book, side, line })), href }, preview };
}
